import { FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL, getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import classNames from "classnames";
import ViewMoreCard from "../common/ViewMoreCard";
import { setAutoResumeCookie } from "../../utils/autoResumeCookie";
import { useInputMode } from "../../utils/hooks/useInputMode";

const aspectGuide = {
  movie: "w-[250px] h-[350px]",
  searchbox: "w-[250px] h-[350px]",
  series: "w-[250px] h-[350px]",
  program: "h-[350px] w-[620px]",
  sport: "h-[350px] w-[620px]",
  music: "w-[250px] h-[250px]",
  animation: "w-[250px] h-[250px]",
  artist: "w-[215px] h-[215px]",
};

const ContentRowScrollingWrapper = styled.div`
  flex-shrink: 1;
  flex-grow: 1;
  padding-left: 60px;
  transition: padding ease 0.2s;
  overflow-x: auto;
  overflow-y: hidden;
`;

const ContentRowScrollingContent = styled.div`
  display: flex;
  transition: all ease 0.5s;
`;

const ContentRowWrapper = styled.div`
  position: relative;
  min-height: 400px;
  background-color: ${({ $kind }) => $kind === "music" ? "#121212" : "transparent"};
  border-radius: ${({ $kind }) => $kind === "music" ? "20px" : "0"};
  width: ${({ $kind }) => ($kind === "music" ? "calc(100% - 5rem)" : "100%")};
  margin: ${({ $kind }) => ($kind === "music" ? "2rem auto" : "2rem 0")};
  ${({ $hasTopTranslate }) => $hasTopTranslate && "transform: translateY(-7rem);"}
  &:after {
    content: "";
    position: absolute;
    width: 312px;
    height: 100%;
    background: ${({ $kind }) => $kind !== "music" ? "linear-gradient(to right, #121212, rgba(0, 0, 0, 0))" : "none"};
    left: 0;
    top: 0;
    pointer-events: none;
  }
`;

const ContentRowTitle = styled.h2`
  color: white;
  margin: 0;
  margin-bottom: 22px;
  font-size: 32px;
  font-weight: 900;
  padding-right: 40px;
  padding-top: 40px;
`;

const CardContainer = styled.div`
  min-width: 215px;
  border-radius: ${props => props.$shapeType === 'artist' ? "50%" : "15px"};
  background-size: cover;
  background-position: center;
  overflow: hidden;
  margin-bottom: 5%;
  position: relative;
  z-index: ${props => props.$focused ? 10 : 0};
  margin: 2px;
  cursor: pointer;
`;

const EpisodeBadge = styled.div`
  position: absolute;
  bottom: ${props => props.$hasProgress ? "24px" : "8px"};
  right: 8px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
  padding: 4px 10px;
  z-index: 15;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
`;

const EpisodeText = styled.span`
  color: white;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const ProgressBarContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  height: 6px;
  background-color: rgb(91, 91, 91);
  border-radius: 11px;
  overflow: hidden;
  z-index: 15;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${props => props.$percent}%;
  background-color: #E50914;
  border-radius: 11px;
  transition: width 0.3s ease;
  margin-right:auto;
`;

const FocusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$shapeType === 'artist' ? "transparent" : "rgba(0,0,0,0.5)"};
  opacity: ${props => props.$focused ? 1 : 0};
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  border-radius: ${props => props.$shapeType === 'artist' ? "50%" : "15px"};
  pointer-events: none;
  transition: opacity 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  flex-direction: column;
  gap: 1rem;
  z-index: 10;
`;

const Title = styled.span`
  color: white;
  font-size: 28px;
  font-weight: 700;
  text-align: center;
`;

const Year = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 400;
  text-align: center;
`;

const MovieItem = ({ item = {}, onFocus, shapeType, isFirst, elapsedTime = 0, totalTime = 0, seasonNumber = null, episodeNumber = null, isContinueWatch = false }) => {
  const navigate = useNavigate();
  const {
    contentPhotos,
    title,
    year,
    slug,
    tags,
    image,
    farsiName,
    kind,
    contentType,
  } = item || {};

  // Get navigation URL - for continue-watch, go directly to player
  const getNavigationUrl = () => {
    if (shapeType === "artist") {
      return `/artists/${slug}`;
    }

    if (isContinueWatch) {
      // Navigate directly to player for continue-watch items
      if (seasonNumber && episodeNumber) {
        // Series: /watch/{slug}/{season}/{episode}
        return `/watch/${slug}/${seasonNumber}/${episodeNumber}`;
      }
      // Movie: /watch/{slug}
      return `/watch/${slug}`;
    }

    // Normal navigation to content page
    return `/${contentType?.kind || kind}/${slug}`;
  };

  // Handle navigation with cookie for continue-watch
  const handleNavigation = () => {
    if (isContinueWatch) {
      // Set encrypted cookie before navigation
      setAutoResumeCookie({
        slug,
        season: seasonNumber ? String(seasonNumber) : null,
        episode: episodeNumber ? String(episodeNumber) : null,
      });
    }
    navigate(getNavigationUrl());
  };

  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: handleNavigation,
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        setFocus("MENU");
        return false;
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support (w=200, q=90)
  const getImageData = () => {
    if (shapeType === "artist") {
      // Artist images may not have LQIP/WebP structure
      return {
        src: getCMSImageURL(image, { width: 200, quality: 90, format: 'jpeg' }),
      };
    } else {
      return getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 200, quality: 90, format: 'jpeg' });
    }
  };

  const imageData = getImageData();

  // Calculate progress percentage for continue-watch
  const progressPercent = totalTime > 0 ? Math.min((elapsedTime / totalTime) * 100, 100) : 0;
  const hasProgress = totalTime > 0 && elapsedTime > 0;

  // Check if this is a series with episode info
  const isSeries = contentType?.kind === "series" || kind === "series";
  const hasEpisodeInfo = seasonNumber && episodeNumber;

  return (
    <div className={shapeType === 'artist' ? "relative pl-5 pt-5 " : "relative ml-3 pt-5"}>
      {isSeries && (
        <div className="absolute bottom-0 top-[-1rem] right-[5px] left-[5px] h-full">
          <img src="/serial_pseudo.svg" className="w-full mt-[1rem]" alt="" />
        </div>
      )}
      <CardContainer
        ref={ref}
        $shapeType={shapeType}
        $focused={focused}
        onClick={handleNavigation}
        onMouseEnter={handleMouseEnter}
        className={classNames("cursor-pointer", aspectGuide[shapeType])}
      >
        <OptimizedImage
          src={imageData.src}
          alt={title}
          className="w-full h-full"
          style={{ borderRadius: shapeType === 'artist' ? "50%" : "15px" }}
        />
        {tags?.[0] && <ContentTag label={tags?.[0]} />}

        {/* Season/Episode badge for series in continue-watch */}
        {hasEpisodeInfo && (
          <EpisodeBadge $hasProgress={hasProgress}>
            <EpisodeText>فصل {seasonNumber} • قسمت {episodeNumber}</EpisodeText>
          </EpisodeBadge>
        )}

        {/* Progress bar for continue-watch */}
        {hasProgress && (
          <ProgressBarContainer>
            <ProgressBarFill $percent={progressPercent} />
          </ProgressBarContainer>
        )}

        {/* Focus overlay with border and content */}
        <FocusOverlay $focused={focused} $shapeType={shapeType}>
          <Title>{title}</Title>
          <Year>{year}</Year>
        </FocusOverlay>
      </CardContainer>
      {farsiName && (
        <p className="text-white mx-auto mt-5 font-bold text-center" style={{fontSize : '24px'}}>
          {farsiName}
        </p>
      )}
    </div>
  );
};

export const MoviesSlider = ({
  movies = [],
  onFocus,
  rowTitle,
  shapeType = "movie",
  id,
  index,
  hasMore = false,
  actionLink = null,
  onMoreTap = null,
  hasTopTranslate = false,
  isContinueWatch = false,
  contentKind,
}) => {
  const { ref, focusKey } = useFocusable({
    onFocus,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusKey: "movie_slider_" + id,
  });
  const [selected, setSelected] = useState(null);

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  // Determine if View More should be shown:
  // - Must have at least 5 items to justify a "view more" option
  // - AND (hasMore flag is true OR content length > 12)
  // - AND actionLink must exist for navigation
  const showViewMore = movies.length >= 5 && (hasMore || movies.length > 12) && actionLink;

  const { isMouseFocus, isMouseFocusRef } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      if (isMouseFocusRef.current || isMouseFocus) return; // Skip horizontal scroll on mouse hover
      const { left } = props;
      if (left < 200) {
        innerRef.current.style.transform = `translateX(${shapeType === "sport" ? Math.abs(left - 500) : Math.abs(left - 300)}px)`;
      } else {
        innerRef.current.style.transform = `translateX(0px)`;
      }
    },
    [scrollingRef, shapeType, isMouseFocusRef, isMouseFocus]
  );

  const paddingOffsetCount = shapeType === "sport" ? 2 : 5;

  if (movies.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper $hasTopTranslate={hasTopTranslate} ref={ref} $kind={shapeType} tabIndex={index}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper
          ref={scrollingRef}
          style={
            selected > paddingOffsetCount && shapeType !== "artist"
              ? { paddingRight: 0 }
              : { paddingRight: 40 }
          }
        >
          <ContentRowScrollingContent ref={innerRef}>
            {movies?.map((item, itemIndex) => {
              // For continue-watch items, content has elapsedTime, totalTime at root level
              // after transformation in useHomeShowcaseData
              const content = item.content || item.artist;
              const itemElapsedTime = item.content?.elapsedTime || 0;
              const itemTotalTime = item.content?.totalTime || 0;
              const itemSeasonNumber = item.content?.seasonNumber || null;
              const itemEpisodeNumber = item.content?.episodeNumber || null;

              return (
                <MovieItem
                  item={content}
                  key={`ms_${item.content?.slug || item.artist?.slug}_${id}_${itemIndex}`}
                  onFocus={(e) => {
                    setSelected(itemIndex);
                    onAssetFocus(e);
                  }}
                  shapeType={shapeType}
                  isFirst={itemIndex === 0}
                  elapsedTime={itemElapsedTime}
                  totalTime={itemTotalTime}
                  seasonNumber={itemSeasonNumber}
                  episodeNumber={itemEpisodeNumber}
                  isContinueWatch={isContinueWatch}
                />
              );
            })}
            {/* View More Card - shown at end when hasMore is true and items >= 10 */}
            {showViewMore && (
              <div className="pt-5">
                <ViewMoreCard
                  actionLink={actionLink}
                  onMoreTap={onMoreTap}
                  shapeType={shapeType}
                  contentKind={contentKind}
                  focusKey={`view_more_${id}`}
                  onFocus={(e) => {
                    setSelected(movies.length);
                    onAssetFocus(e);
                  }}
                />
              </div>
            )}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
